import { EyeIcon, PencilEdit02Icon, TrashIcon } from '@/icons';
import { Button, Space, type TableProps } from 'antd';
import { Link } from 'react-router';
import {
  useDeleteDepartmentMutation,
  useGetAllDepartmentsQuery,
} from '@/app/features/employee/emp-dashboard/emp-dashboard.api';
import ConfirmationModal from '@/components/ConfirmationModal';
import CustomTable from '@/components/CustomTable';
import StatusTag from '@/components/StatusTag';
import {
  DISCLOSER_KEYS,
  FILTER_KEYS,
} from '@/constants/keys.constants';
import { useAppDiscloser, useAppFilters } from '@/hooks';
import { getErrorMessage } from '@/lib/error';
import { toast } from '@/lib/toast';
import DepartmentUsers from './_components/Departments/DepartmentUsers';
import ManageDepartment from './_components/Departments/ManageDepartment';

const DEPARTMENT_STATUS_CONFIG = [
  { label: 'Active', value: 'active', className: 'status status-success' },
  { label: 'Inactive', value: 'inactive', className: 'status status-error' },
];

const Departments = () => {
  // Use discloser hook for ManageDepartment drawer
  const [config, handleOpen, handleClose] = useAppDiscloser(
    DISCLOSER_KEYS.ORG_DEPARTMENTS,
  );

  // Use filters hook
  const { page, limit, filters, handlePagination } = useAppFilters(
    FILTER_KEYS.ORG_DEPARTMENTS,
  );

  // Fetch departments data
  const {
    data: departmentsData,
    isLoading,
    isFetching,
  } = useGetAllDepartmentsQuery({
    params: { page, limit, ...filters },
  });

  // Delete department
  const [deleteDepartment, { isLoading: isDeleting }] =
    useDeleteDepartmentMutation();

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    try {
      await deleteDepartment({ id: config?.data?.id }).unwrap();
      toast('success', 'Department deleted successfully');
      handleClose();
    } catch (error: any) {
      const errorMessage = getErrorMessage(
        error,
        'Failed to delete department',
      );
      toast('error', errorMessage);
    }
  };

  const columns: TableProps<any>['columns'] = [
    {
      title: 'Department Name',
      dataIndex: 'name',
      render: (val: string) => val || 'n/a',
    },

    {
      title: 'Created Date',
      dataIndex: 'created_at',
      render: (val) => (val ? new Date(val).toLocaleDateString() : 'n/a'),
    },
    {
      title: 'Assigned Employees',
      dataIndex: 'total_users',
      render: (val) => val ?? 'n/a',
    },

    {
      title: 'Status',
      dataIndex: 'status',
      align: 'center',
      render: (status: string) => (
        <StatusTag value={status} config={DEPARTMENT_STATUS_CONFIG} />
      ),
    },
    {
      title: 'Actions',
      align: 'end',
      render: (record: any) => (
        <Space>
          <Link to={``}>
            <Button
              size="small"
              className="action-btn"
              icon={<EyeIcon size={20} />}
              onClick={() => handleOpen({ type: 'VIEW', data: record })}
            />
          </Link>

          <Button
            size="small"
            className="action-btn"
            icon={<PencilEdit02Icon size={20} />}
            onClick={() => handleOpen({ type: 'UPDATE', data: record })}
          />

          <Button
            size="small"
            className="action-btn"
            icon={<TrashIcon size={20} />}
            onClick={() => handleOpen({ type: 'DELETE', data: record })}
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <CustomTable
        page={page}
        limit={limit}
        hideRowSelect
        columns={columns}
        loading={isLoading || isFetching}
        onPaginate={handlePagination}
        tableRootClassName="border-0 p-0"
        dataSource={departmentsData?.data?.data || []}
        total={departmentsData?.data?.meta?.total || 0}
      />

      <ManageDepartment
        data={config.data}
        type={config.type}
        open={
          config.open && (config.type === 'ADD' || config.type === 'UPDATE')
        }
        id={config.data?.id}
        handleClose={handleClose}
      />

      <DepartmentUsers
        data={config.data}
        handleClose={handleClose}
        open={config.open && config.type === 'VIEW'}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        open={config.open && config.type === 'DELETE'}
        handleClose={handleClose}
        handleSubmit={handleDeleteConfirm}
        loading={isDeleting}
        title="Delete Department"
        subTitle={`Are you sure you want to delete ${config?.data?.name}? This action cannot be undone.`}
        buttonName="Delete"
      />
    </div>
  );
};

export default Departments;
